import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Paper,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Button,
    CircularProgress,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CancelIcon from '@mui/icons-material/Cancel';
import { analysisService } from '../../services';
import { ProcessingProgress } from '../../types';
import { formatDuration } from '../../utils/formatters';

const ProcessingPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const analysisId = (location.state as { analysisId?: string })?.analysisId;

    const [progress, setProgress] = useState<ProcessingProgress | null>(null);
    const [isComplete, setIsComplete] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    // Subscribe to progress updates
    useEffect(() => {
        if (!analysisId) {
            // No analysis ID, redirect to upload
            navigate('/upload');
            return;
        }

        const unsubscribe = analysisService.onProgress(analysisId, (newProgress) => {
            setProgress(newProgress);

            // Check if complete
            if (newProgress.overallProgress >= 100) {
                setIsComplete(true);
                setTimeout(() => {
                    navigate('/results', { state: { analysisId } });
                }, 1000);
            }
        });

        return () => {
            unsubscribe();
        };
    }, [analysisId, navigate]);

    const handleCancel = useCallback(async () => {
        if (!analysisId) return;
        setIsCancelling(true);
        await analysisService.cancelAnalysis(analysisId);
        navigate('/upload');
    }, [analysisId, navigate]);

    // Get step icon based on status
    const getStepIcon = (status: string, isActive: boolean) => {
        if (status === 'complete') {
            return <CheckCircleIcon sx={{ color: 'success.main' }} />;
        }
        if (status === 'active' || isActive) {
            return (
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                    <AutorenewIcon sx={{ color: 'primary.main' }} />
                </motion.div>
            );
        }
        return <RadioButtonUncheckedIcon sx={{ color: 'grey.400' }} />;
    };

    if (!analysisId) {
        return null;
    }

    return (
        <Box
            sx={{
                minHeight: '80vh',
                py: { xs: 4, md: 8 },
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Container maxWidth="md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <AnimatePresence mode="wait">
                            {!isComplete ? (
                                <motion.div
                                    key="processing"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <Box sx={{ mb: 3 }}>
                                        <CircularProgress size={60} thickness={4} />
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                                        Processing Analysis...
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Please wait, the system is analyzing your file
                                    </Typography>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="complete"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                >
                                    <Box sx={{ mb: 3 }}>
                                        <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main' }} />
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
                                        Analysis Complete!
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Redirecting to results page...
                                    </Typography>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>

                    {/* Progress Bar */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            mb: 4,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Overall Progress
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="primary.main">
                                    {progress?.overallProgress ?? 0}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress?.overallProgress ?? 0}
                                sx={{
                                    height: 12,
                                    borderRadius: 2,
                                    backgroundColor: 'grey.200',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 2,
                                        background: isComplete
                                            ? 'linear-gradient(90deg, #2e7d32 0%, #4caf50 100%)'
                                            : 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                                    },
                                }}
                            />
                        </Box>

                        {progress?.estimatedTimeRemaining !== undefined && !isComplete && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                                Estimated time remaining: {formatDuration(progress.estimatedTimeRemaining)}
                            </Typography>
                        )}
                    </Paper>

                    {/* Processing Steps */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                            Processing Steps
                        </Typography>

                        <Stepper orientation="vertical" activeStep={progress?.currentStep ?? 0}>
                            {progress?.steps.map((step, index) => (
                                <Step key={step.id} completed={step.status === 'complete'}>
                                    <StepLabel
                                        StepIconComponent={() => getStepIcon(step.status, index === (progress?.currentStep ?? 0) - 1)}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: step.status === 'active' ? 600 : 400,
                                                color: step.status === 'complete' ? 'success.main' : 'text.primary',
                                            }}
                                        >
                                            {step.name}
                                        </Typography>
                                    </StepLabel>
                                    <StepContent>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {step.description}
                                        </Typography>
                                        {step.status === 'active' && (
                                            <LinearProgress
                                                variant="determinate"
                                                value={step.progress}
                                                sx={{ height: 4, borderRadius: 2 }}
                                            />
                                        )}
                                    </StepContent>
                                </Step>
                            ))}
                        </Stepper>
                    </Paper>

                    {/* Cancel Button */}
                    {!isComplete && (
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={isCancelling ? <CircularProgress size={16} /> : <CancelIcon />}
                                onClick={handleCancel}
                                disabled={isCancelling}
                                sx={{
                                    borderWidth: 2,
                                    '&:hover': { borderWidth: 2 },
                                }}
                            >
                                {isCancelling ? 'Cancelling...' : 'Cancel Analysis'}
                            </Button>
                        </Box>
                    )}
                </motion.div>
            </Container>
        </Box>
    );
};

export default ProcessingPage;
